import { useState, useCallback, useEffect } from 'react';

import cn from 'classnames';

import { currencySymbol } from 'libs/string';

import { PriceProduct } from 'ui/atoms/PriceProduct/PriceProduct';
import { Button } from 'ui/atoms/Button/Button';

import { PricingProductDescription } from 'features/PAYG/organisms/PricingBlock/PricingProductDescription';
import { ModalFormBook } from 'features/articles/organisms/ModalFormBook';

import styles from './PricingBlockCards.module.scss';

import { prices } from './data';
import type { PricingProduct, PricingProductPrice } from './PricingBlock.types';

const baseClass = 'pricing-block-cards';

export type Props = {
  loading: boolean;
  data: (PricingProduct & PricingProductPrice)[];
  theme?: 'light' | 'dark';
  className?: string;
  currency: keyof typeof prices;
  hasFeatureKeys?: boolean;
};

export const PricingBlockCards = ({ loading, data, theme = 'light', className, currency, hasFeatureKeys }: Props) => {
  const [origin, setOrigin] = useState<string>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setOrigin(window.location.href);
  }, []);

  const onToggleModal = useCallback(() => setShowModal((s) => !s), []);

  return (
    <>
      {showModal && <ModalFormBook title="Contact sales" maxWidth={630} onClose={onToggleModal} />}

      <div className={cn(styles[`${baseClass}-wrapper`], className)}>
        <div className={styles[`${baseClass}-inner`]}>
          {data.map((card, i) => (
            <div
              key={i}
              data-test="pricing-card"
              className={cn(
                styles[`${baseClass}-card`],
                styles[`${baseClass}-${theme}`],
                card.best_value && styles[`${baseClass}-best`],
              )}
            >
              {card.best_value && <div className={styles[`${baseClass}-label`]}>Most Popular</div>}

              <div>
                <div>
                  <div className={styles[`${baseClass}-header`]}>
                    <div className={styles[`${baseClass}-text`]}>
                      <div className={styles[`${baseClass}-title`]}>
                        <h2>{card.name}</h2>
                      </div>

                      <span className={styles[`${baseClass}-description`]}>{card.description}</span>
                    </div>

                    <PriceProduct
                      size="sm"
                      currency={currencySymbol(currency)}
                      value={card.price}
                      valueExplanation={card.price_explanation}
                      loading={card.type !== 'custom' && loading}
                    />
                  </div>

                  {hasFeatureKeys && card.keyFeature && (
                    <div className={styles[`${baseClass}-key-feature`]}>
                      <div className={styles[`${baseClass}-key-feature-value`]}>{card.keyFeature.value}</div>
                      <div className={styles[`${baseClass}-key-feature-title`]}>{card.keyFeature.title}</div>
                    </div>
                  )}
                </div>

                <PricingProductDescription data={card.pricing_groups} theme={theme} />

                <Button
                  size="xl"
                  type={(card.best_value && 'blue') || (card.type === 'custom' && 'dark-outline') || 'dark'}
                  className={styles[`${baseClass}-next-step`]}
                  {...(card.type === 'custom'
                    ? {
                        onClick: onToggleModal,
                      }
                    : {
                        target: '_blank',
                        rel: 'noreferrer',
                        href: origin ? `${card.link}${card.link.includes('?') ? '&' : '?'}origin=${origin}` : card.link,
                      })}
                >
                  {card.button_label}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
